import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { DiffModeEnum, DiffView } from "@git-diff-view/react";
import { DiffFile, generateDiffFile } from "@git-diff-view/file";
import "@git-diff-view/react/styles/diff-view.css";
import styles from "./selfServiceSettings.module.scss";
import { ErrorToast, SuccessToast } from "./selfServiceSettings";
import { TextWithTooltip } from "../../components/TextWithTooltip";

class ScribeContentError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "ScribeContentError";
    }
}

export function SelfServiceUploaderPage({ ujID }: { ujID: number }) {
    const [newFileContent, setNewFileContent] = useState<string | null>(null);
    const [existingFileContent, setExistingFileContent] = useState<
        string | null
    >(null);
    const [diffFile, setDiffFile] = useState<DiffFile | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [newVersion, setNewVersion] = useState(false);
    const [newVersionMessage, setNewVersionMessage] = useState("");

    const getAndSetExistingFileContent = useCallback(() => {
        fetch(`/api/v1/uj/${ujID}/scribe`)
            .then((response) => {
                if (!response.ok) {
                    throw new ScribeContentError(
                        "Failure getting existing scribe content, contact Tribe support.",
                    );
                }
                return response.text();
            })
            .then((existingScribeContent) => {
                setExistingFileContent(existingScribeContent);
            })
            .catch((error) => {
                if (error instanceof ScribeContentError) {
                    setErrorMessage(error.message);
                } else {
                    setErrorMessage(
                        "An unexpected error occurred while fetching existing scribe content. Please try again later.",
                    );
                }
            });
    }, [ujID]);

    const resetMessages = useCallback(() => {
        setShowSuccess(false);
        setErrorMessage("");
    }, []);

    const resetState = useCallback(() => {
        getAndSetExistingFileContent();
        setNewFileContent(null);
        setDiffFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setNewVersion(false);
        setNewVersionMessage("");
    }, [getAndSetExistingFileContent]);

    useEffect(() => {
        resetState();
        resetMessages();
    }, [resetMessages, resetState, ujID]);

    function createUploadDiff(e: React.ChangeEvent<HTMLInputElement>) {
        resetMessages();
        const file = e.target.files?.[0];

        if (!file) {
            setNewFileContent(null);
            setDiffFile(null);
            return;
        }

        if (file.size > 256 * 1024) {
            setErrorMessage(
                "File size exceeds the 256KB limit. Please select a smaller file.",
            );
            return;
        }

        if (existingFileContent === null) {
            setErrorMessage(
                "Failed to load existing scribe content. Please refresh the page.",
            );
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            const content = event.target?.result;
            if (typeof content === "string") {
                const currentDiffFile = generateDiffFile(
                    "old.scribe",
                    existingFileContent,
                    "new.scribe",
                    content,
                    "yaml",
                    "yaml",
                );
                currentDiffFile.initTheme("light");
                currentDiffFile.init();
                currentDiffFile.buildSplitDiffLines();

                setNewFileContent(content);
                setDiffFile(currentDiffFile);
            }
        };

        reader.readAsText(file);
    }

    function uploadFileContent() {
        if (!newFileContent) {
            setErrorMessage(
                "No file selected. Please select a file to upload.",
            );
            return;
        }

        const formData = new FormData();
        formData.set(
            "scribe",
            new Blob([newFileContent], { type: "text/plain" }),
            `JourneyScribe_${ujID}.scribe`,
        );

        if (newVersion) {
            if (!newVersionMessage) {
                setErrorMessage(
                    "New version message is required when 'New Version' is selected.",
                );
                return;
            }

            formData.set("newVersionMessage", newVersionMessage);
        }

        const response = fetch(`/api/v1/uj/${ujID}/scribe`, {
            method: "POST",
            body: formData,
        });

        response
            .then(async (response) => {
                if (response.ok) {
                    return response.json();
                } else if (response.status === 400) {
                    // try and provide a friendly way of displaying the error message returned from the server
                    const errorMessage = await response.text();
                    const parsedMessage = JSON.parse(errorMessage);
                    if (parsedMessage.errors) {
                        const userFriendlyErrorMessage = parsedMessage.errors
                            .map((e: { message: string }) => e.message)
                            .join("\n");
                        throw new Error(userFriendlyErrorMessage);
                    } else if (parsedMessage.message) {
                        throw new Error(parsedMessage.message);
                    } else {
                        throw new Error(errorMessage);
                    }
                } else {
                    throw new Error(
                        "Failed to upload scribe. Please try again.",
                    );
                }
            })
            .then((data) => {
                if (data.status === "ok") {
                    setShowSuccess(true);
                    resetState();
                } else {
                    setErrorMessage(
                        "Failed to upload scribe. Please try again.",
                    );
                }
            })
            .catch((error: Error) => {
                setErrorMessage(error.message);
            });
    }

    return (
        <>
            <Form className={styles.formGroup}>
                <Form.Label htmlFor={"scribeUpload"}>
                    Upload a new Scribe:
                </Form.Label>
                <Form.Control
                    id={"scribeUpload"}
                    type={"file"}
                    onChange={createUploadDiff}
                    ref={fileInputRef}
                />
            </Form>

            {diffFile && (
                <div className={styles.formGroup}>
                    {existingFileContent === newFileContent ? (
                        <h3>No changes detected.</h3>
                    ) : (
                        existingFileContent !== newFileContent && (
                            <DiffView
                                diffFile={diffFile}
                                diffViewMode={DiffModeEnum.Unified}
                            />
                        )
                    )}
                </div>
            )}

            <Form.Group className={styles.formGroup}>
                <Form.Label htmlFor={"newVersionCheckbox"}>
                    <TextWithTooltip name={"New Version"} />
                </Form.Label>
                <Form.Check
                    type={"switch"}
                    id={"newVersionCheckbox"}
                    checked={newVersion}
                    onChange={(e) => setNewVersion(e.target.checked)}
                />
                {newVersion && (
                    <Form.Group>
                        <Form.Label htmlFor={"newVersionMessage"}>
                            <TextWithTooltip name={"New Version Message"} />
                        </Form.Label>
                        <Form.Control
                            type={"text"}
                            id={"newVersionMessage"}
                            value={newVersionMessage}
                            onChange={(e) =>
                                setNewVersionMessage(e.target.value)
                            }
                            required={newVersion}
                            maxLength={300}
                        ></Form.Control>
                    </Form.Group>
                )}
            </Form.Group>

            <Button variant={"tribe-primary"} onClick={uploadFileContent}>
                Upload
            </Button>

            <Button variant={"secondary"} href={`/api/v1/uj/${ujID}/scribe`}>
                Download Existing Scribe
            </Button>

            {showSuccess && (
                <SuccessToast
                    clearToast={() => setShowSuccess(false)}
                    successMessage={"Scribe successfully uploaded!"}
                />
            )}

            {errorMessage && (
                <ErrorToast
                    errorMessage={errorMessage}
                    clearToast={() => setErrorMessage("")}
                />
            )}
        </>
    );
}
